import { FC } from 'react';
import { LinkType } from './LinksFormInput';
import { Dialog, InputLabel, Stack, TextField, useTheme } from '@mui/material';
import { ModalContent } from '../../modal/components/ModalContent';
import { ControlledFormField } from '../../form/ControlledFormField';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import { ModalActions } from '../../modal/components/ModalActions';
import { IntlShape, useIntl } from 'react-intl';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { z } from 'zod';

const createLinkSchema = (intl: IntlShape) =>
	z.object({
		value: z
			.string()
			.refine(
				(value) => /^(http(s)?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]+(?:\/[^\s]*)?$/.test(value || ''),
				intl.formatMessage({ id: 'form.error.link-format' }),
			),
		text: z.string().optional(),
	});

type Props = Readonly<{
	close: () => void;
	onChange: (link: LinkType) => void;
	value?: string;
	text?: string;
}>;

type FormValues = z.infer<ReturnType<typeof createLinkSchema>>;

export const ManageLinkModal: FC<Props> = ({ close, onChange, value, text }) => {
	const intl = useIntl();
	const theme = useTheme();

	const form = useTypedForm({
		schema: createLinkSchema(intl),
		defaultValues: {
			value: value ?? '',
			text: text ?? '',
		},
	});

	const onSubmit = ({ value, text }: FormValues) => {
		form.reset();

		if (value !== '') {
			const linkWithoutProtocol = value.replace(/(http)s?:\/\//, '').trim();
			onChange({ link: linkWithoutProtocol, text });
			close();
		}
	};

	return (
		<Dialog maxWidth={'xs'} open={true}>
			<ModalContent title={intl.formatMessage({ id: 'modals.link.create.title' })}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<Stack gap={1.5}>
						<ControlledFormField
							form={form}
							name={'value'}
							label={''}
							render={(props) => (
								<Stack direction={'row'} flexWrap={'nowrap'} alignItems={'stretch'} flexGrow={1}>
									<InputLabel
										sx={{
											py: 1.25,
											px: 2,
											backgroundColor: theme.palette[getNightOrDayPalette(theme)][50],
											border: `1px solid ${theme.palette[getNightOrDayPalette(theme)][200]}`,
											borderTopLeftRadius: '4px',
											borderBottomLeftRadius: '4px',
											borderRight: 0,
											flexShrink: 0,
										}}
										//eslint-disable-next-line formatjs/no-literal-string-in-jsx
									>
										https://
									</InputLabel>
									<TextField
										{...props}
										placeholder={intl.formatMessage({ id: 'common.link.placeholder' })}
										variant={'outlined'}
										autoFocus
										sx={{
											flexGrow: 1,
											'.MuiInputBase-root': {
												borderTopLeftRadius: 0,
												borderBottomLeftRadius: 0,
												p: 0,
												height: '100%',
											},
											input: {
												py: 1.25,
												px: 2,
											},
											fieldset: {
												borderColor: theme.palette[getNightOrDayPalette(theme)][200],
											},
										}}
										hiddenLabel
									/>
								</Stack>
							)}
						/>
						<ControlledFormField
							form={form}
							name={'text'}
							label={''}
							render={(props) => (
								<TextField
									{...props}
									placeholder={intl.formatMessage({ id: 'common.link.text.placeholder' })}
									variant={'outlined'}
									sx={{
										flexGrow: 1,
										'.MuiInputBase-root': {
											borderTopLeftRadius: 0,
											borderBottomLeftRadius: 0,
											p: 0,
											height: '100%',
										},
										input: {
											py: 1.25,
											px: 2,
										},
										fieldset: {
											borderColor: theme.palette[getNightOrDayPalette(theme)][200],
										},
									}}
									hiddenLabel
								/>
							)}
						/>
					</Stack>
				</form>
			</ModalContent>
			<ModalActions
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				handleCancelButtonClick={close}
				primaryButtonText={'Submit'}
				cancelButtonText={'Cancel'}
			/>
		</Dialog>
	);
};
